/**
 * Small building blocks of the cycle planning page (#1522).
 */

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import {
  Badge,
  Button,
  Caption1,
  Input,
  Link,
  Subtitle1,
  Textarea,
  Tooltip,
  makeStyles,
  tokens,
  type InputProps,
  type TextareaProps,
} from '@fluentui/react-components'
import { Clock16Regular, Info16Regular } from '@fluentui/react-icons'
import type { PlanChip as Chip } from '../../types/planning'

const TONE: Record<string, 'brand' | 'danger' | 'warning' | 'success' | 'informative'> = {
  accent: 'brand',
  red: 'danger',
  amber: 'warning',
  teal: 'success',
}

export function PlanChip({ chip }: { chip?: Chip | null }) {
  if (!chip) return null
  return (
    <Badge appearance="tint" size="small" color={TONE[chip[1]] ?? 'informative'}>
      {chip[0]}
    </Badge>
  )
}

/** The explanation behind an ⓘ: a focusable button that carries the text as its label. */
export function InfoHint({ text }: { text: string }) {
  return (
    <Tooltip content={text} relationship="label" withArrow>
      <Button appearance="transparent" size="small" icon={<Info16Regular />} />
    </Tooltip>
  )
}

/**
 * Base and number both come from an imported file, so the finished address is
 * checked, not its parts: only an https GitHub address may become a link.
 */
function issueHref(base: string | undefined, n: number): string | null {
  if (!base || !Number.isInteger(n)) return null
  try {
    const url = new URL(`${base}${n}`)
    return url.protocol === 'https:' && url.hostname === 'github.com' ? url.href : null
  } catch {
    return null
  }
}

/** An issue number that opens GitHub in a new tab. */
export function IssueLink({ n, base }: { n: number; base?: string }) {
  const href = issueHref(base, n)
  if (!href) return <span>#{n}</span>
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      #{n}
      <span aria-hidden="true"> ↗</span>
    </Link>
  )
}

/**
 * Text that saves on blur or Enter, not on every keystroke: the page polls,
 * and saving each key would let a refresh overwrite what is being typed.
 * Only typing makes the draft this user's; until then a refresh shows through,
 * so focusing a box and leaving it never sends someone else's old value back.
 * Escape puts the saved value back.
 */
function useCommitDraft(value: string, onCommit: (v: string) => void) {
  const [draft, setDraft] = useState(value)
  const typed = useRef(false)
  useEffect(() => {
    if (!typed.current) setDraft(value)
  }, [value])
  return {
    value: draft,
    onChange: (_: unknown, d: { value: string }) => {
      typed.current = true
      setDraft(d.value)
    },
    onBlur: () => {
      if (typed.current && draft.trim() !== value.trim()) onCommit(draft.trim())
      typed.current = false
    },
    onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        e.currentTarget.blur()
      }
      if (e.key === 'Escape') {
        typed.current = false
        setDraft(value)
        e.currentTarget.blur()
      }
    },
  }
}

type Committed = { value: string; onCommit: (v: string) => void }
type Owned = 'value' | 'onChange' | 'onBlur' | 'onKeyDown'

export function CommitInput({ value, onCommit, ...rest }: Committed & Omit<InputProps, Owned>) {
  return <Input {...rest} {...useCommitDraft(value, onCommit)} />
}

export function CommitTextarea({
  value,
  onCommit,
  ...rest
}: Committed & Omit<TextareaProps, Owned>) {
  return <Textarea {...rest} {...useCommitDraft(value, onCommit)} />
}

const useSectionStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    // Room for the sticky agenda, so a section scrolled to starts below it.
    scrollMarginTop: '64px',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // The icon on a tinted disc, with the agenda's number on it: the room sees
  // which agenda item this is from across the table (#1578).
  icon: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '36px',
    height: '36px',
    borderRadius: tokens.borderRadiusCircular,
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: '20px',
  },
  step: {
    position: 'absolute',
    top: '-4px',
    right: '-6px',
    minWidth: '18px',
    height: '18px',
    padding: '0 4px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground2}`,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1,
  },
  time: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorNeutralForeground3,
  },
  spacer: { flexGrow: 1 },
  // Focused by script only (the agenda, Present's next section): it is not a
  // control, so no focus ring, which a shared screen shows as a stray box.
  heading: { ':focus': { outlineStyle: 'none' } },
})

export function PlanSection({
  id,
  icon,
  step,
  title,
  minutes,
  hint,
  draft,
  extra,
  children,
}: {
  id: string
  icon: ReactNode
  /** Its number in the agenda. */
  step?: number
  title: string
  minutes?: number
  hint?: string
  draft?: boolean
  extra?: ReactNode
  children: ReactNode
}) {
  const styles = useSectionStyles()
  return (
    <section id={id} className={styles.section} aria-labelledby={`h-${id}`}>
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden="true" data-testid={`step-${id}`}>
          {icon}
          {step != null && <span className={styles.step}>{step}</span>}
        </span>
        {/* Focusable by script only, so the agenda can move the keyboard here. */}
        <Subtitle1 as="h2" id={`h-${id}`} tabIndex={-1} className={styles.heading}>
          {title}
        </Subtitle1>
        {draft && (
          <Tooltip content="The room's changes, not on GitHub yet. Apply to GitHub writes them." relationship="description">
            <Badge appearance="outline" size="small" color="informative" tabIndex={0}>
              Draft
            </Badge>
          </Tooltip>
        )}
        {minutes != null && (
          <Caption1 className={styles.time}>
            <Clock16Regular aria-hidden="true" />
            {minutes} min
          </Caption1>
        )}
        {hint && <InfoHint text={hint} />}
        <span className={styles.spacer} />
        {extra}
      </div>
      {children}
    </section>
  )
}

/** The agenda's pills: the page's sticky bar and Present mode's top bar. */
export const useAgendaStyles = makeStyles({
  agenda: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    padding: 0,
    margin: 0,
    listStyle: 'none',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  // The section on screen, so a glance at the agenda says where the meeting is.
  here: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { background: tokens.colorBrandBackground2Hover },
  },
  // The same numbered disc as on each section's icon.
  num: {
    minWidth: '18px',
    height: '18px',
    padding: '0 4px',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1,
  },
})

export const usePanelStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
  },
  panelHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightSemibold,
  },
  list: { margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '4px' },
  missing: { color: tokens.colorPaletteRedForeground1, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
})
